import React from 'react';

const nf = (v: number) => v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
const formatINR = (v: number) => isFinite(v) ? `₹${nf(v)}` : '-';
const formatCompact = (v: number) => {
    if (!isFinite(v)) return '-';
    if (Math.abs(v) >= 100000) return `₹${Math.round(v / 100000)} L`;
    return formatINR(v);
};

function emiMonthly(principal: number, annualRate: number, years: number) {
    if (principal <= 0 || years <= 0) return 0;
    const r = annualRate / 12 / 100;
    const n = years * 12;
    if (r === 0) return principal / n;
    return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

const RentVsBuyCalculator: React.FC = () => {
    const [monthlyRent, setMonthlyRent] = React.useState<number>(20000);
    const [annualRentIncrease, setAnnualRentIncrease] = React.useState<number>(3);

    const [purchasePrice, setPurchasePrice] = React.useState<number>(5000000);
    const [downPaymentPct, setDownPaymentPct] = React.useState<number>(20);
    const [loanInterest, setLoanInterest] = React.useState<number>(7.5);
    const [loanTenure, setLoanTenure] = React.useState<number>(20);
    const [annualAppreciation, setAnnualAppreciation] = React.useState<number>(4);
    const [holdingYears, setHoldingYears] = React.useState<number>(10);
    const [annualMaintenance, setAnnualMaintenance] = React.useState<number>(10000);

    // compute
    const downPayment = (downPaymentPct / 100) * purchasePrice;
    const loanAmount = Math.max(0, purchasePrice - downPayment);
    const monthlyEmi = emiMonthly(loanAmount, loanInterest, loanTenure);

    // Rent totals over period
    const rentTotals = React.useMemo(() => {
        let total = 0;
        let monthly = monthlyRent;
        for (let y = 0; y < holdingYears; y++) {
            total += monthly * 12;
            monthly = monthly * (1 + annualRentIncrease / 100);
        }
        return total;
    }, [monthlyRent, annualRentIncrease, holdingYears]);

    // Buy totals over period (approx): sum of EMIs for holding years + maintenance - appreciation
    const buyTotals = React.useMemo(() => {
        const years = holdingYears;
        const annualMortgage = monthlyEmi * 12;
        const totalMortgagePaid = annualMortgage * Math.min(years, loanTenure);
        const totalMaintenance = annualMaintenance * years;
        const purchaseCost = downPayment + totalMortgagePaid + totalMaintenance;
        const futureValue = purchasePrice * Math.pow(1 + annualAppreciation / 100, years);
        const netGain = futureValue - purchaseCost;
        return { annualMortgage, totalMortgagePaid, totalMaintenance, purchaseCost, futureValue, netGain };
    }, [monthlyEmi, loanTenure, holdingYears, annualMaintenance, downPayment, purchasePrice, annualAppreciation]);

    const diffAnnual = buyTotals.annualMortgage + annualMaintenance - (rentTotals / holdingYears);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 purple-card-shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                <div className="space-y-4">
                    <h3 className="text-xl font-bold text-[#0b3b82]">Rent Inputs</h3>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Monthly Rent (₹)</label>
                        <input type="number" value={monthlyRent} onChange={(e) => setMonthlyRent(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatINR(monthlyRent)} / month</div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Annual Rent Increase (%)</label>
                        <input type="number" value={annualRentIncrease} onChange={(e) => setAnnualRentIncrease(Number(e.target.value) || 0)} className="w-28 border rounded p-2 text-sm" />
                    </div>

                    <hr className="my-3" />

                    <h3 className="text-xl font-bold text-[#0b3b82]">Buy Inputs</h3>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Purchase Price (₹)</label>
                        <input type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatCompact(purchasePrice)}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold mb-1">Down Payment (%)</label>
                            <input type="number" value={downPaymentPct} onChange={(e) => setDownPaymentPct(Number(e.target.value) || 0)} className="w-full border rounded p-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">Loan Interest (%)</label>
                            <input type="number" value={loanInterest} onChange={(e) => setLoanInterest(Number(e.target.value) || 0)} className="w-full border rounded p-2 text-sm" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold mb-1">Loan Tenure (years)</label>
                            <input type="number" value={loanTenure} onChange={(e) => setLoanTenure(Number(e.target.value) || 0)} className="w-full border rounded p-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1">Holding Period (years)</label>
                            <input type="number" value={holdingYears} onChange={(e) => setHoldingYears(Number(e.target.value) || 0)} className="w-full border rounded p-2 text-sm" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Annual Maintenance (₹)</label>
                        <input type="number" value={annualMaintenance} onChange={(e) => setAnnualMaintenance(Number(e.target.value) || 0)} className="w-full border rounded p-3 text-sm" />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Annual Appreciation (%)</label>
                        <input type="number" value={annualAppreciation} onChange={(e) => setAnnualAppreciation(Number(e.target.value) || 0)} className="w-28 border rounded p-2 text-sm" />
                    </div>
                </div>

                <div>
                    <div className="rounded-xl border-8 border-[#001b4a] p-6 bg-white shadow-md h-full">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-extrabold text-lg text-[#001b4a]">Summary</h3>
                            <div className="text-xl font-extrabold text-[#0b3b82]">{formatCompact(buyTotals.futureValue)}</div>
                        </div>

                        <div className="border-t border-dashed border-slate-200 pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Total Rent Paid ({holdingYears}y)</div>
                                <div className="font-semibold text-slate-800">{formatINR(rentTotals)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Total Mortgage Paid ({Math.min(loanTenure, holdingYears)}y)</div>
                                <div className="font-semibold text-slate-800">{formatINR(buyTotals.totalMortgagePaid)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Total Maintenance ({holdingYears}y)</div>
                                <div className="font-semibold text-slate-800">{formatINR(buyTotals.totalMaintenance)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Net Gain (est.)</div>
                                <div className={`font-semibold ${buyTotals.netGain >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{isFinite(buyTotals.netGain) ? formatINR(buyTotals.netGain) : '-'}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Annual cost difference (buy - rent)</div>
                                <div className={`font-semibold ${diffAnnual >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{isFinite(diffAnnual) ? formatINR(diffAnnual) : '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RentVsBuyCalculator;
